import type { IRegion } from "@agrigate/location-types";
import useAsyncTask from "@agrigate/shared/lib/hooks/use-async-task";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import type { Combobox, ComboboxRootProps } from "@chakra-ui/react";
import _ from "lodash";
import type { ElementType } from "react";
import { type ReactElement, useMemo } from "react";
import ComboboxSelect from "./ComboSelect";
import { getRPC } from "./utils/rpc";

interface RegionDropdownProps {
    name?: string;
    label?: string;
    regionId?: MaybeN<string>;
    defaultValue?: MaybeN<string>;
    onRegionInit?: (region: IRegion) => void;
    onRegionChange?: (regionId?: string, regionName?: string) => void;
    autofetchInitial?: MaybeN<boolean>;
    allowedRegionIds?: string[];
    disabled?: boolean;
    width?: string;
    customCaret?: ElementType;
    placement?: ComboboxRootProps["placeContent"];
    portalled?: boolean;
    isDesktop?: boolean;
    comboBoxProps?: Omit<Combobox.ControlProps, "children">;
    comboInputProps?: Omit<Combobox.InputProps, "children">;
    comboRootProps?: Omit<
        Combobox.RootProps,
        | "children"
        | "collection"
        | "onInputValueChange"
        | "value"
        | "onValueChange"
    >;
}
export default function RegionDropDown(p: RegionDropdownProps): ReactElement {
    const { regionId, onRegionChange, label, ...props } = p;
    const fetchRegionsT = useAsyncTask(
        async () => {
            const res = await getRPC().location.findAllRegions({});
            if (p.regionId) {
                const region = res.regions.find(_ => _.id === p.regionId);
                if (region) p.onRegionInit?.(region);
            }
            return res.regions;
        },
        {
            autoRun: true,
            deps: [],
        },
    );

    const regionOptions = useMemo(() => {
        const regions = p.allowedRegionIds?.length
            ? fetchRegionsT.data?.filter(r =>
                  p.allowedRegionIds!.includes(r.id),
              )
            : fetchRegionsT.data;

        return regions?.map(item => ({
            label: _.capitalize(item.name),
            value: item.id,
        }));
    }, [fetchRegionsT.data, p.allowedRegionIds]);

    return (
        <ComboboxSelect
            label={label}
            placeholder="Select Region"
            items={regionOptions ?? []}
            value={fetchRegionsT.data && regionId ? [regionId] : []}
            onChange={selectedIds => {
                const selectedId = selectedIds[0];
                const region = selectedId
                    ? fetchRegionsT?.data?.find(r => r.id === selectedId)
                    : null;

                onRegionChange?.(region?.id, region?.name);
                fetchRegionsT.trigger();
            }}
            width={p.width ?? "20%"}
            caretAs={p.customCaret}
            comboRootProps={{
                disabled: p.disabled,
                ...p.comboRootProps,
            }}
            portalled={p.portalled ?? true}
            placement={p.placement}
            comboBoxProps={{ ...p.comboBoxProps }}
            comboInputProps={{ ...p.comboInputProps }}
        />
    );
}
